import moment from 'moment';
import client from '../../libs/client';
import axios from 'axios';

export default class BillingNewGateway {
    getBillingUrlBase = (operationId) => {
        return `/operations/${operationId}/billing`;
    };
    getProducerBillingUrlBase = (participantId) => {
        return `/participants/${participantId}/billing`;
    };

    getBillableEntities = async (operationId) => {
        const { data: billableEntities } = await client.get(
            `${this.getBillingUrlBase(operationId)}/billable-entities`
        );
        return billableEntities;
    };

    getBillingEntities = async (operationId) => {
        const { data: billingEntities } = await client.get(
            `${this.getBillingUrlBase(operationId)}/billing-entities`
        );
        return billingEntities;
    };
    getProducerBillingEntities = async (participantId) => {
        const { data: billingEntities } = await client.get(
            `${this.getProducerBillingUrlBase(participantId)}/billing-entities`
        );
        return billingEntities;
    };

    createBillingEntity = async (operationId, billingEntity) => {
        const res = await client.post(
            `${this.getBillingUrlBase(operationId)}/billing-entities`,
            billingEntity
        );
        return res;
    };

    updateBillingEntity = async (operationId, billingEntity) => {
        const res = await client.put(
            `${this.getBillingUrlBase(operationId)}/billing-entities/${
                billingEntity.id
            }`,
            billingEntity
        );
        return res;
    };

    createBillableEntity = async (operationId, billableEntity) => {
        const res = await client.post(
            `${this.getBillingUrlBase(operationId)}/billable-entities`,
            billableEntity
        );
        return res;
    };

    updateBillableEntity = async (operationId, billableEntity) => {
        const res = await client.put(
            `${this.getBillingUrlBase(operationId)}/billable-entities/${
                billableEntity.id
            }`,
            billableEntity
        );
        return res;
    };

    getInvoices = async (operationId) => {
        const { data: invoices } = await client.get(
            `${this.getBillingUrlBase(operationId)}/invoices`
        );
        return invoices;
    };

    getLastInvoiceDate = async (
        operationId,
        billingEntityId,
        billableEntityId
    ) => {
        const { data: lastInvoiceDate } = await client.get(
            `${this.getBillingUrlBase(
                operationId
            )}/invoices/lastInvoiceDate?billingEntityId=${billingEntityId}&billableEntityId=${billableEntityId}
            `
        );
        return lastInvoiceDate;
    };

    getInvoiceDownloadURL = async (
        operationId,
        invoiceId,
        language,
        isLegacyBill
    ) => {
        const { data: url } = await client.get(
            `${this.getBillingUrlBase(
                operationId
            )}/invoices/${invoiceId}/download?language=${
                language ? language : ''
            }&isLegacyBill=${isLegacyBill}`
        );
        return url;
    };

    cancelInvoice = async (operationId, invoiceId) => {
        const res = await client.post(
            `${this.getBillingUrlBase(
                operationId
            )}/invoices/cancel?ids=${invoiceId}`
        );
        return res;
    };
    cancelParticipantInvoice = async (participantId, invoiceId) => {
        const res = await client.post(
            `${this.getProducerBillingUrlBase(
                participantId
            )}/invoices/cancel?ids=${invoiceId}`
        );
        return res;
    };

    changeInvoicePaymentStatus = async (
        operationId,
        invoiceId,
        paymentStatus
    ) => {
        const res = await client.put(
            `${this.getBillingUrlBase(
                operationId
            )}/invoices/${invoiceId}/edit-payment-status`,
            {
                status: paymentStatus,
            }
        );
        return res;
    };
    changeParticipantInvoicePaymentStatus = async (
        participantId,
        invoiceId,
        paymentStatus
    ) => {
        const res = await client.put(
            `${this.getProducerBillingUrlBase(
                participantId
            )}/invoices/${invoiceId}/edit-payment-status`,
            {
                status: paymentStatus,
            }
        );
        return res;
    };

    getAccountingSummaryDownloadURL = async (operationId, invoicesIds) => {
        const url = `${this.getBillingUrlBase(
            operationId
        )}/invoices/accounting-summary?invoicesIds=${invoicesIds.join(',')}`;
        return `${process.env.REACT_APP_API_URL}${url}`;
    };

    getProducerAccountingSummaryDownloadURL = async (
        participantId,
        invoicesIds
    ) => {
        const url = `${this.getProducerBillingUrlBase(
            participantId
        )}/invoices/accounting-summary?invoicesIds=${invoicesIds.join(',')}`;
        return `${process.env.REACT_APP_API_URL}${url}`;
    };

    generateInvoices = async (operationId, date, mode) => {
        const res = await client.post(
            `${this.getBillingUrlBase(operationId)}/run`,
            {
                billUpTo: date ? date : moment().format('YYYY-MM-DD'),
                mode,
            }
        );
        return res.data;
    };

    getGenerationProgress = async (operationId, jobId) => {
        const res = await client.get(
            `${this.getBillingUrlBase(operationId)}/run/${jobId}`
        );
        const billRun = res.data;

        return billRun.progress;
    };

    getParticipantGenerationProgress = async (participantId, jobId) => {
        const res = await client.get(
            `${this.getProducerBillingUrlBase(participantId)}/run/${jobId}`
        );
        const billRun = res.data;

        return billRun.progress;
    };

    deleteInvoices = async (operationId, invoiceIds) => {
        const res = await client.delete(
            `${this.getBillingUrlBase(
                operationId
            )}/invoices?ids=${invoiceIds.join(',')}`
        );
        return res;
    };

    deleteProducerInvoices = async (participantId, invoiceIds) => {
        const res = await client.delete(
            `${this.getProducerBillingUrlBase(
                participantId
            )}/invoices?ids=${invoiceIds.join(',')}`
        );
        return res;
    };

    publishInvoices = async (operationId, invoiceIds) => {
        const res = await client.post(
            `${this.getBillingUrlBase(
                operationId
            )}/invoices/publish?ids=${invoiceIds.join(',')}`
        );
        return res;
    };

    publishProducerInvoices = async (participantId, invoiceIds) => {
        const res = await client.post(
            `${this.getProducerBillingUrlBase(
                participantId
            )}/invoices/publish?ids=${invoiceIds.join(',')}`
        );
        return res;
    };
    getInvoicesZip = async (operationId, invoicesIds, billsIds) => {
        const res = await client.get(
            `${this.getBillingUrlBase(
                operationId
            )}/invoices/download-zip?invoicesIds=${invoicesIds.join(
                ','
            )}&billsIds=${billsIds.join(',')}`,
            { responseType: 'blob' }
        );
        return res;
    };
    getProducerInvoicesZip = async (producerId, invoicesIds, billsIds) => {
        const res = await client.get(
            `participants/${producerId}/billing/invoices/download-zip?invoicesIds=${invoicesIds.join(
                ','
            )}&billsIds=${billsIds.join(',')}`,
            { responseType: 'blob' }
        );
        return res;
    };

    getBillingPlans = async (operationId) => {
        const { data: billingPlans } = await client.get(
            `${this.getBillingUrlBase(operationId)}/plans`
        );
        return billingPlans;
    };

    createBillingPlan = async (operationId, billingPlan) => {
        const res = await client.post(
            `${this.getBillingUrlBase(operationId)}/plans`,
            billingPlan
        );
        return res;
    };

    updateBillingPlan = async (operationId, billingPlan) => {
        const res = await client.put(
            `${this.getBillingUrlBase(operationId)}/plans/${billingPlan.id}`,
            billingPlan
        );
        return res;
    };

    getBillingSubscriptions = async (operationId) => {
        const { data: billingSubscriptions } = await client.get(
            `${this.getBillingUrlBase(operationId)}/subscriptions`
        );
        return billingSubscriptions;
    };
    getParticipantBillingSubscriptions = async (producerId) => {
        const { data: billingSubscriptions } = await client.get(
            `participants/${producerId}/billing/subscriptions`
        );
        return billingSubscriptions;
    };

    createMultipleSubscriptions = async (
        operationId,
        multipleSubscriptions
    ) => {
        const res = await client.post(
            `${this.getBillingUrlBase(operationId)}/subscriptions/multiple`,
            multipleSubscriptions
        );
        return res.data;
    };
    createProducerMultipleSubscriptions = async (
        producerId,
        multipleSubscriptions
    ) => {
        const res = await client.post(
            `participants/${producerId}/billing/subscriptions/multiple`,
            multipleSubscriptions
        );
        return res.data;
    };

    createBillingSubscription = async (operationId, billingSubscription) => {
        const res = await client.post(
            `${this.getBillingUrlBase(operationId)}/subscriptions`,
            billingSubscription
        );
        return res.data;
    };

    deleteBillingSubscription = async (operationId, subscriptionId) => {
        const res = await client.delete(
            `${this.getBillingUrlBase(
                operationId
            )}/subscriptions/${subscriptionId}`
        );
        return res.data;
    };

    deleteProducerBillingSubscription = async (producerId, subscriptionId) => {
        const res = await client.delete(
            `participants/${producerId}/billing/subscriptions/${subscriptionId}`
        );
        return res.data;
    };

    getBillingEntityLogo = async (billingEntity, operation) => {
        let res;
        if (billingEntity && billingEntity.billingLogo) {
            const {
                data: { url },
            } = await client.get(
                `/operations/${operation?.id}/billing/billing-entities/${billingEntity.id}/logo`
            );
            const { data: logo } = await axios(url, {
                method: 'GET',
                responseType: 'blob',
            });
            res = URL.createObjectURL(logo);
        }
        return res;
    };
    uploadBillingEntityLogo = async (billingEntity, operation, formData) => {
        const res = await client.post(
            `/operations/${operation?.id}/billing/billing-entities/${billingEntity.id}/logo`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return res.data;
    };
    getLastInvoicesRun = async (operationId) => {
        const res = await client.get(
            `/operations/${operationId}/billing/run/last`
        );
        return res.data;
    };
    getParticipantLastInvoicesRun = async (participantId) => {
        const res = await client.get(
            `/participants/${participantId}/billing/run/last`
        );
        return res.data;
    };
}
